<template>
  <div class="games">
    <h1 class="visually-hidden">{{ getContent(currentLocaleData, defaultLocaleData, 'IPageMeta.title') }}</h1>

    <client-only>
      <main-slider v-if="filteredSlider.length" :slides="filteredSlider" />
    </client-only>

    <slider-nav-collections v-if="collectionViews.length" :items="collectionViews" @clickCategory="changeCategory" />

    <client-only>
      <form-input-search
        v-model:value="searchValue"
        :hasClearIcon="!!searchValue"
        :placeholder="getContent(layoutData, defaultLocaleLayoutData, 'header.search.placeholder')"
      />
    </client-only>

    <div class="games-main">
      <group-games-adaptive
        v-if="!collectionsLoaded || topGameCollection"
        key="top"
        :searchValue="searchValue"
        showAllBtn
        showArrows
        :category="topGameCollection"
      />

      <group-games-adaptive
        v-if="!collectionsLoaded || hotGamesCollection"
        key="hot"
        :searchValue="searchValue"
        showAllBtn
        showArrows
        :category="hotGamesCollection"
        icon-color="red"
      />

      <group-winners />

      <group-games-adaptive
        v-if="!collectionsLoaded || newGameCollection"
        key="new"
        :searchValue="searchValue"
        showAllBtn
        showArrows
        :category="newGameCollection"
      />

      <group-providers show-arrows show-all-btn />

      <group-games-adaptive key="all" :searchValue="searchValue" showAllBtn showArrows :category="allGameCollection" />
    </div>
  </div>
</template>

<script setup lang="ts">
  import type { IHomePage } from '~/types';
  import type { ICollection } from '@skeleton/core/types';

  const dayjs = useDayjs();

  const router = useRouter();

  const { layoutData, defaultLocaleLayoutData, gameCategoriesObj } = storeToRefs(useGlobalStore());
  const { localizePath } = useProjectMethods();
  const { getContent } = useProjectMethods();
  const profileStore = useProfileStore();
  const { isTelegramUser, isLoggedIn } = storeToRefs(profileStore);

  const { getContentData: getSliderContentData } = useContentLogic<IHomePage>({
    contentKey: 'casinoPageContent',
    contentRoute: ['pages', 'casino'],
    isPage: true,
  });
  const { data: sliderContent } = await useLazyAsyncData(getSliderContentData);

  const currentLocaleData = computed(() => sliderContent.value?.currentLocaleData);
  const defaultLocaleData = computed(() => sliderContent.value?.defaultLocaleData);

  const { getCollectionsList, findGameCollectionByIdentity } = useGamesStore();
  const { data: gameCollections } = await useLazyAsyncData(() => getCollectionsList(), { server: false });

  const filteredSlider = computed(() => {
    if (!sliderContent.value?.currentLocaleData?.slider) {
      return [];
    }

    const { filteredSlider } = useSliderFilter({
      slider: sliderContent.value.currentLocaleData.slider,
      isLoggedIn: isLoggedIn.value,
      isTelegramUser: isTelegramUser.value,
      sliderFilterTime: dayjs.utc(),
    });

    return filteredSlider.value;
  });

  const searchValue = ref('');

  const collectionsLoaded = computed(() => Array.isArray(gameCollections.value));

  const filteredCollection = computed(() => gameCollections.value?.filter(collection => !collection.isHidden) || []);

  const collectionViews = computed(() => {
    return filteredCollection.value?.map(item => {
      const findCollection = gameCategoriesObj.value[item.identity.toLowerCase()];

      return {
        ...item,
        ...findCollection,
        label: findCollection?.label || item.name,
      };
    });
  });

  const topGameCollection = computed(() => getGameCollection('top'));
  const hotGamesCollection = computed(() => getGameCollection('hot'));
  const newGameCollection = computed(() => getGameCollection('new'));
  const allGameCollection = computed(() => getGameCollection('All'));

  const getGameCollection = (identity: string): ICollection | undefined => {
    return findGameCollectionByIdentity(gameCollections.value || [], identity);
  };

  const changeCategory = (categoryId: string) => {
    router.push({
      path: localizePath(`/games/${categoryId}`),
    });
  };
</script>

<style src="~/assets/styles/pages/games/index.scss" lang="scss" />
